/**
 * OIDC auth via Keycloak — Authorization Code flow with PKCE.
 *
 * Keycloak URL is configurable via AUTH_CONFIG.keycloakUrl.
 * Tokens are stored in sessionStorage under 'pbx_*' keys.
 */

import type { AuthConfig, TokenPayload, AppUser, AuthObject } from './types/auth';

export const AUTH_CONFIG: AuthConfig = {
    keycloakUrl: import.meta.env.VITE_KEYCLOAK_URL || 'https://k.nir.gov.ua',
    realm: 'master',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'pbx-web',
    redirectUri: window.location.origin + '/callback.html',
};

/** Computed base URL for Keycloak realm endpoints */
function realmUrl(): string {
    return `${AUTH_CONFIG.keycloakUrl}/realms/${AUTH_CONFIG.realm}`;
}

let _user: AppUser | null = null;

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

function generateRandomString(len: number = 64): string {
    const array = new Uint8Array(len);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

async function sha256(plain: string): Promise<ArrayBuffer> {
    const data = new TextEncoder().encode(plain);
    return crypto.subtle.digest('SHA-256', data);
}

function base64urlEncode(buf: ArrayBuffer): string {
    return btoa(String.fromCharCode(...new Uint8Array(buf)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
    const hash = await sha256(verifier);
    return base64urlEncode(hash);
}

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

export function getToken(): string | null {
    return sessionStorage.getItem('pbx_access_token') || null;
}

function getRefreshToken(): string | null {
    return sessionStorage.getItem('pbx_refresh_token') || null;
}

function storeTokens(accessToken: string, refreshToken?: string): void {
    sessionStorage.setItem('pbx_access_token', accessToken);
    if (refreshToken) {
        sessionStorage.setItem('pbx_refresh_token', refreshToken);
    }
}

function clearTokens(): void {
    sessionStorage.removeItem('pbx_access_token');
    sessionStorage.removeItem('pbx_refresh_token');
    sessionStorage.removeItem('pbx_user');
    sessionStorage.removeItem('pkce_verifier');
    sessionStorage.removeItem('pkce_state');
}

// ---------------------------------------------------------------------------
// JWT decode (no verification — just payload extraction)
// ---------------------------------------------------------------------------

export function getTokenPayload(token?: string): TokenPayload | null {
    if (!token) token = getToken() as string | undefined;
    if (!token) return null;
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(payload));
    } catch {
        return null;
    }
}

function isTokenExpired(token: string): boolean {
    const payload = getTokenPayload(token);
    if (!payload || !payload.exp) return true;
    // Consider expired 30 seconds before actual expiry
    return payload.exp - 30 < Date.now() / 1000;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function isAuthenticated(): boolean {
    const token = getToken();
    if (!token) return false;
    return !isTokenExpired(token);
}

export function getUserRoles(): string[] {
    const payload = getTokenPayload();
    if (!payload) return [];
    return payload.realm_access?.roles || [];
}

/**
 * Redirect to Keycloak login page (Authorization Code + PKCE).
 */
export async function login(): Promise<void> {
    const verifier = generateRandomString(64);
    const challenge = await generateCodeChallenge(verifier);
    const state = generateRandomString(32);

    sessionStorage.setItem('pkce_verifier', verifier);
    sessionStorage.setItem('pkce_state', state);

    const params = new URLSearchParams({
        client_id: AUTH_CONFIG.clientId,
        redirect_uri: AUTH_CONFIG.redirectUri,
        response_type: 'code',
        scope: 'openid profile email',
        state: state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
    });

    window.location.href = `${realmUrl()}/protocol/openid-connect/auth?${params}`;
}

/**
 * Exchange authorization code for tokens.
 * Called from callback.html.
 */
export async function exchangeCode(code: string): Promise<any> {
    const verifier = sessionStorage.getItem('pkce_verifier');
    if (!verifier) throw new Error('Missing PKCE verifier');

    const resp = await fetch(`${realmUrl()}/protocol/openid-connect/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: AUTH_CONFIG.clientId,
            code: code,
            redirect_uri: AUTH_CONFIG.redirectUri,
            code_verifier: verifier,
        }),
    });

    if (!resp.ok) {
        const err = await resp.text();
        throw new Error(`Token exchange failed: ${resp.status} ${err}`);
    }

    const data = await resp.json();
    storeTokens(data.access_token, data.refresh_token);

    // Clean up PKCE storage
    sessionStorage.removeItem('pkce_verifier');
    sessionStorage.removeItem('pkce_state');

    return data;
}

/**
 * Use refresh_token to obtain a new access_token.
 * Returns the new access token, or null on failure.
 */
export async function refreshToken(): Promise<string | null> {
    const rt = getRefreshToken();
    if (!rt) return null;

    try {
        const resp = await fetch(`${realmUrl()}/protocol/openid-connect/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: AUTH_CONFIG.clientId,
                refresh_token: rt,
            }),
        });

        if (!resp.ok) {
            console.warn('Token refresh failed:', resp.status);
            clearTokens();
            return null;
        }

        const data = await resp.json();
        storeTokens(data.access_token, data.refresh_token);
        return data.access_token;
    } catch (e: any) {
        console.warn('Token refresh error:', (e instanceof Error ? e.message : String(e)));
        clearTokens();
        return null;
    }
}

/**
 * Logout — clear local state and redirect to Keycloak logout.
 */
export function logout(): void {
    const idToken = getToken(); // Used for id_token_hint if available
    clearTokens();
    _user = null;

    const params = new URLSearchParams({
        post_logout_redirect_uri: window.location.origin,
        client_id: AUTH_CONFIG.clientId,
    });

    window.location.href = `${realmUrl()}/protocol/openid-connect/logout?${params}`;
}

// ---------------------------------------------------------------------------
// auth object — backward-compatible singleton used by app.js, router.js, etc.
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Token auto-refresh scheduler
// ---------------------------------------------------------------------------

let _refreshTimer: number | null = null;

function scheduleTokenRefresh(): void {
    if (_refreshTimer) clearTimeout(_refreshTimer);
    const token = getToken();
    if (!token) return;
    const payload = getTokenPayload(token);
    if (!payload?.exp) return;

    const expiresIn = payload.exp * 1000 - Date.now();
    const refreshAt = expiresIn - 60 * 1000; // 1 min before expiry

    if (refreshAt > 0) {
        _refreshTimer = window.setTimeout(async () => {
            const newToken = await refreshToken();
            if (newToken) {
                scheduleTokenRefresh();
            } else {
                _showSessionWarning();
            }
        }, refreshAt);
    }
}

function _showSessionWarning(): void {
    const container = document.getElementById("toast-container");
    if (!container || container.querySelector(".session-warning")) return;
    const el = document.createElement("div");
    el.className = "toast error session-warning";
    el.style.cssText = "cursor:pointer;font-weight:600";
    el.innerHTML = "&#9888; Сесія закінчується. <u>Натисніть щоб продовжити</u>";
    el.addEventListener("click", async () => {
        el.remove();
        const newToken = await refreshToken();
        if (newToken) {
            scheduleTokenRefresh();
            const ok = document.createElement("div");
            ok.className = "toast success";
            ok.textContent = "Сесію продовжено";
            container.appendChild(ok);
            setTimeout(() => { ok.style.opacity = "0"; setTimeout(() => ok.remove(), 300); }, 3000);
        } else {
            login();
        }
    });
    container.appendChild(el);
}

export const auth: AuthObject = {
    /**
     * Called on startup by app.js.
     * If authenticated, returns user info extracted from JWT.
     * If not authenticated, returns null (caller should call login()).
     */
    async init(): Promise<AppUser | null> {
        // Check for stored token
        const token = getToken();
        if (token && !isTokenExpired(token)) {
            _user = _buildUserFromToken(token);
            scheduleTokenRefresh();
            return _user;
        }

        // Try refreshing
        if (getRefreshToken()) {
            const newToken = await refreshToken();
            if (newToken) {
                _user = _buildUserFromToken(newToken);
                scheduleTokenRefresh();
                return _user;
            }
        }

        // Not authenticated
        return null;
    },

    login,
    logout,
    getToken,
    getUser(): AppUser | null {
        return _user;
    },
    setUser(u: AppUser): void {
        _user = u;
    },
    isAuthenticated,
    getTokenPayload,
    getUserRoles,
    refreshToken,

    isAdmin(): boolean {
        if (!_user) return false;
        return _user.roles?.includes('admin') || _user.roles?.includes('pbx-admin') || _user.role === 'admin' || false;
    },

    isDeptHead(): boolean {
        if (!_user) return false;
        return _user.roles?.includes('dept-head') || this.isAdmin() || false;
    },
};

function _buildUserFromToken(token: string): AppUser | null {
    const payload = getTokenPayload(token);
    if (!payload) return null;
    const user: AppUser = {
        sub: payload.sub,
        name: payload.name || payload.preferred_username || '',
        username: payload.preferred_username || '',
        email: payload.email || '',
        roles: payload.realm_access?.roles || [],
    };
    sessionStorage.setItem('pbx_user', JSON.stringify(user));
    return user;
}
